import {
  ASTEROID_START_SIZE,
  ASTEROID_DENSITY,
  SHIP_MASS,
  BULLET_MASS,
  SHIP_MAX_ROTATION_SPEED,
  SHIELD_BULLET_VELOCITY_TRANSFER,
  SHIELD_BULLET_SPIN_GAIN,
  SHIELD_ASTEROID_RESTITUTION,
  SHIELD_ASTEROID_VELOCITY_TRANSFER,
  SHIELD_ASTEROID_SPIN_GAIN
} from './constants.js';

function shortestWrappedDelta(delta, span) {
  if (!span) return delta;
  if (delta > span / 2) return delta - span;
  if (delta < -span / 2) return delta + span;
  return delta
}

function isToroidal(worldBounds) {
  return worldBounds?.toroidalMap !== false;
}

function wrappedSpan(worldBounds, axis) {
  if (!isToroidal(worldBounds)) return null;
  return axis === 'x' ? worldBounds?.width : worldBounds?.height;
}

// Treat exact tangential contact as a hit (inclusive), with tiny float slack.
const COLLISION_EPSILON_SQ = 1e-6

/**
 * Torus-aware circle metrics: `marginSq` is distSq − (rA+rB)².
 * Negative means overlap; zero is grazing (exclusive in {@link collides}).
 */
export function circleCircleMarginSq(a, b, worldBounds = null) {
  const dx = shortestWrappedDelta(
    a.position.x - b.position.x,
    wrappedSpan(worldBounds, 'x')
  );
  const dy = shortestWrappedDelta(
    a.position.y - b.position.y,
    wrappedSpan(worldBounds, 'y')
  );
  const distSq = dx * dx + dy * dy
  const sum = a.radius + b.radius
  const reachSq = sum * sum
  return { distSq, reachSq, marginSq: distSq - reachSq, dx, dy, sum }
}

function pointSegmentDistSq(px, py, x1, y1, x2, y2) {
  const vx = x2 - x1
  const vy = y2 - y1
  const wx = px - x1
  const wy = py - y1
  const vv = vx * vx + vy * vy
  if (vv <= 0) return wx * wx + wy * wy
  let t = (wx * vx + wy * vy) / vv
  if (t < 0) t = 0
  else if (t > 1) t = 1
  const cx = x1 + vx * t
  const cy = y1 + vy * t
  const dx = px - cx
  const dy = py - cy
  return dx * dx + dy * dy
}

/**
 * Swept bullet-vs-target check over one tick.
 * Treats movement as a segment and tests segment-to-center distance against
 * combined radius, using torus-shortest deltas so seam crossings are continuous.
 */
export function sweptCircleCollides(
  fromPos,
  toPos,
  moverRadius,
  target,
  worldBounds = null
) {
  const spanX = wrappedSpan(worldBounds, 'x')
  const spanY = wrappedSpan(worldBounds, 'y')
  const dx = shortestWrappedDelta(toPos.x - fromPos.x, spanX)
  const dy = shortestWrappedDelta(toPos.y - fromPos.y, spanY)
  const tx = fromPos.x + shortestWrappedDelta(target.position.x - fromPos.x, spanX)
  const ty = fromPos.y + shortestWrappedDelta(target.position.y - fromPos.y, spanY)
  const distSq = pointSegmentDistSq(tx, ty, fromPos.x, fromPos.y, fromPos.x + dx, fromPos.y + dy)
  const sum = moverRadius + target.radius
  const reachSq = sum * sum
  return { hit: distSq <= reachSq + COLLISION_EPSILON_SQ, distSq, reachSq, marginSq: distSq - reachSq }
}

/**
 * Swept collision with both circles moving during the tick.
 * Works in relative space by tracing bullet-vs-asteroid center delta from
 * tick start to tick end and testing that segment against origin.
 */
export function sweptMovingCircleCollides(
  moverFrom,
  moverTo,
  moverRadius,
  targetFrom,
  targetTo,
  targetRadius,
  worldBounds = null
) {
  const spanX = wrappedSpan(worldBounds, 'x')
  const spanY = wrappedSpan(worldBounds, 'y')

  const dx0 = shortestWrappedDelta(moverFrom.x - targetFrom.x, spanX)
  const dy0 = shortestWrappedDelta(moverFrom.y - targetFrom.y, spanY)
  const dx1Raw = shortestWrappedDelta(moverTo.x - targetTo.x, spanX)
  const dy1Raw = shortestWrappedDelta(moverTo.y - targetTo.y, spanY)

  // Keep relative segment continuous across seams.
  const dx1 = dx0 + shortestWrappedDelta(dx1Raw - dx0, spanX)
  const dy1 = dy0 + shortestWrappedDelta(dy1Raw - dy0, spanY)

  const distSq = pointSegmentDistSq(0, 0, dx0, dy0, dx1, dy1)
  const sum = moverRadius + targetRadius
  const reachSq = sum * sum
  return { hit: distSq <= reachSq + COLLISION_EPSILON_SQ, distSq, reachSq, marginSq: distSq - reachSq }
}

// Circle-circle collision (instantaneous centers only; no swept segment).
export function collides(a, b, worldBounds = null) {
  const { distSq, reachSq } = circleCircleMarginSq(a, b, worldBounds)
  return distSq <= reachSq + COLLISION_EPSILON_SQ
}

// Same as collides() but with explicit radii — lets callers extend a hit zone (e.g. shields)
// without mutating the entity's own radius.
export function collidesWithRadius(a, b, ra, rb, worldBounds = null) {
  const dx = shortestWrappedDelta(
    a.position.x - b.position.x,
    wrappedSpan(worldBounds, 'x')
  )
  const dy = shortestWrappedDelta(
    a.position.y - b.position.y,
    wrappedSpan(worldBounds, 'y')
  )
  const distSq = dx * dx + dy * dy
  const sum = ra + rb
  return distSq <= sum * sum + COLLISION_EPSILON_SQ
}

/**
 * One-tick bullet vs moving asteroid: overlap at `instantBulletPos` vs `instantAstPos` (defaults
 * to `astCurPos`), bullet segment vs rock at `astPosForStaticSwept` (defaults to `astCurPos`),
 * and both-moving sweep (rock `astPrevPos` → `astCurPos`). Main tick omits optional rock poses;
 * spawn lag-comp sets `instantBulletPos`/`instantAstPos`/`astPosForStaticSwept` to the rewind
 * layout start.
 * @param {{
 *   bulletPrevPos: { x: number, y: number },
 *   bulletCurPos: { x: number, y: number },
 *   bulletRadius: number,
 *   instantBulletPos: { x: number, y: number },
 *   instantAstPos?: { x: number, y: number },
 *   astPrevPos: { x: number, y: number },
 *   astCurPos: { x: number, y: number },
 *   astRadius: number,
 *   astPosForStaticSwept?: { x: number, y: number }
 * }} p
 * @param {{ width: number, height: number }} bounds
 */
export function bulletAsteroidHitThisTick(p, bounds) {
  const instantAstPos = p.instantAstPos ?? p.astCurPos;
  const instantAst = { position: instantAstPos, radius: p.astRadius };
  const staticSweptPos = p.astPosForStaticSwept ?? p.astCurPos;
  const astStaticSwept = { position: staticSweptPos, radius: p.astRadius };
  const instantBullet = { position: p.instantBulletPos, radius: p.bulletRadius };
  const instantHit = collides(instantBullet, instantAst, bounds);
  const swept = sweptCircleCollides(p.bulletPrevPos, p.bulletCurPos, p.bulletRadius, astStaticSwept, bounds);
  const sweptRelative = sweptMovingCircleCollides(
    p.bulletPrevPos,
    p.bulletCurPos,
    p.bulletRadius,
    p.astPrevPos,
    p.astCurPos,
    p.astRadius,
    bounds
  );
  return instantHit || swept.hit || sweptRelative.hit
}

/**
 * Wrap a position onto a toroidal world of size (w, h) with optional padding.
 * `pad` is the radius by which an entity may travel past the edge before wrap
 * — matches the original asteroid-style wrap feel.
 */
export function wrap(position, w, h, pad = 0) {
  if (position.x > w + pad) position.x = -pad;
  else if (position.x < -pad) position.x = w + pad;
  if (position.y > h + pad) position.y = -pad;
  else if (position.y < -pad) position.y = h + pad
}

function setSpinValue(state, value) {
  if ('angularVelocity' in state) state.angularVelocity = value;
  else state.rotationSpeed = value;
}

function getSpinValue(state) {
  if ('angularVelocity' in state) return state.angularVelocity ?? 0;
  return state.rotationSpeed ?? 0;
}

function bounceAgainstNormal(state, normal, {
  restitution = 1,
  tangentialFriction = 1,
  spinCoupling = 0,
  spinMassFactor = 1,
  spinDamping = 1
} = {}) {
  const vx = state.velocity.x;
  const vy = state.velocity.y;
  const vn = vx * normal.x + vy * normal.y;
  if (vn >= 0) return false;

  const vnx = normal.x * vn;
  const vny = normal.y * vn;
  const tx = vx - vnx;
  const ty = vy - vny;

  state.velocity.x = tx * tangentialFriction - vnx * restitution;
  state.velocity.y = ty * tangentialFriction - vny * restitution;

  const torque = normal.x * ty - normal.y * tx;
  const spin = getSpinValue(state) * spinDamping + torque * spinCoupling * spinMassFactor;
  setSpinValue(state, spin);
  return true;
}

export function asteroidWallSpinMassFactor(radius) {
  if (!Number.isFinite(radius) || radius <= 0) return 1;
  return Math.max(0.5, ASTEROID_START_SIZE / (radius * 2));
}

export function bounceCircleOffWalls(state, worldBounds, options = {}) {
  if (isToroidal(worldBounds)) return false;

  const radius = state.radius ?? 0;
  const minX = radius;
  const maxX = worldBounds.width - radius;
  const minY = radius;
  const maxY = worldBounds.height - radius;
  let bounced = false;

  if (state.position.x < minX) {
    state.position.x = minX;
    bounced = bounceAgainstNormal(state, { x: 1, y: 0 }, options) || bounced;
  } else if (state.position.x > maxX) {
    state.position.x = maxX;
    bounced = bounceAgainstNormal(state, { x: -1, y: 0 }, options) || bounced;
  }

  if (state.position.y < minY) {
    state.position.y = minY;
    bounced = bounceAgainstNormal(state, { x: 0, y: 1 }, options) || bounced;
  } else if (state.position.y > maxY) {
    state.position.y = maxY;
    bounced = bounceAgainstNormal(state, { x: 0, y: -1 }, options) || bounced;
  }

  return bounced;
}

// Clamp angle to [0, 360).
export function normalizeDeg(deg) {
  let d = deg % 360;
  if (d < 0) d += 360;
  return d
}

// Effective mass of an asteroid from its radius (area × density).
export function asteroidMass(radius) {
  if (!Number.isFinite(radius) || radius <= 0) return 0
  return ASTEROID_DENSITY * Math.PI * radius * radius
}

function clampSpin(value) {
  if (value > SHIP_MAX_ROTATION_SPEED) return SHIP_MAX_ROTATION_SPEED
  if (value < -SHIP_MAX_ROTATION_SPEED) return -SHIP_MAX_ROTATION_SPEED
  return value
}

// Wrapped-aware contact normal pointing from `from` toward `to`. Returns unit vector;
// falls back to a nominal normal when the centers coincide.
function contactNormal(from, to, worldBounds) {
  const dx = shortestWrappedDelta(to.x - from.x, wrappedSpan(worldBounds, 'x'))
  const dy = shortestWrappedDelta(to.y - from.y, wrappedSpan(worldBounds, 'y'))
  const len = Math.hypot(dx, dy)
  if (len <= 1e-6) return { x: 1, y: 0 }
  return { x: dx / len, y: dy / len }
}

// Bullet hits a shielded ship: nudge ship velocity along the contact normal proportional
// to bullet mass and closing speed; tangential component imparts spin (glancing → max spin,
// dead-on → none). Mutates the ship in place.
export function applyShieldImpulseFromBullet(ship, bullet, worldBounds = null) {
  const n = contactNormal(bullet.position, ship.position, worldBounds)
  const vrx = bullet.velocity.x - ship.velocity.x
  const vry = bullet.velocity.y - ship.velocity.y
  const vn = vrx * n.x + vry * n.y
  if (vn <= 0) return false

  const j = (2 * BULLET_MASS * SHIP_MASS) / (BULLET_MASS + SHIP_MASS) * vn
  ship.velocity.x += (j / SHIP_MASS) * n.x * SHIELD_BULLET_VELOCITY_TRANSFER
  ship.velocity.y += (j / SHIP_MASS) * n.y * SHIELD_BULLET_VELOCITY_TRANSFER

  const vtx = vrx - vn * n.x
  const vty = vry - vn * n.y
  const tMag = Math.hypot(vtx, vty)
  if (tMag > 1e-6) {
    const cross = n.x * vry - n.y * vrx
    const sign = cross >= 0 ? 1 : -1
    ship.angularVelocity = clampSpin(
      ship.angularVelocity + sign * tMag * SHIELD_BULLET_SPIN_GAIN
    )
  }
  return true
}

// Asteroid hits a shielded ship: bigger rocks land harder. Reflects ship velocity along
// the contact normal with restitution, tangential component imparts spin scaled by mass ratio.
export function applyShieldImpulseFromAsteroid(ship, asteroid, worldBounds = null) {
  const n = contactNormal(asteroid.position, ship.position, worldBounds)
  const m = asteroidMass(asteroid.radius)
  if (m <= 0) return false
  const vrx = asteroid.velocity.x - ship.velocity.x
  const vry = asteroid.velocity.y - ship.velocity.y
  const vn = vrx * n.x + vry * n.y
  const closing = vn > 0 ? vn : 0

  // Conservation-of-momentum impulse along normal, plus restitution to ensure ship bounces away.
  const j = (1 + SHIELD_ASTEROID_RESTITUTION) * (m * SHIP_MASS) / (m + SHIP_MASS) * Math.max(closing, 0)
  ship.velocity.x += (j / SHIP_MASS) * n.x * SHIELD_ASTEROID_VELOCITY_TRANSFER
  ship.velocity.y += (j / SHIP_MASS) * n.y * SHIELD_ASTEROID_VELOCITY_TRANSFER

  // Reflect any inward component of ship velocity so it doesn't penetrate the asteroid.
  const shipVn = ship.velocity.x * n.x + ship.velocity.y * n.y
  if (shipVn < 0) {
    ship.velocity.x -= (1 + SHIELD_ASTEROID_RESTITUTION) * shipVn * n.x
    ship.velocity.y -= (1 + SHIELD_ASTEROID_RESTITUTION) * shipVn * n.y
  }

  const vtx = vrx - vn * n.x
  const vty = vry - vn * n.y
  const tMag = Math.hypot(vtx, vty)
  if (tMag > 1e-6) {
    const cross = n.x * vry - n.y * vrx
    const sign = cross >= 0 ? 1 : -1
    ship.angularVelocity = clampSpin(
      ship.angularVelocity + sign * tMag * SHIELD_ASTEROID_SPIN_GAIN * (m / SHIP_MASS)
    )
  }
  return true
}
